import { TransitionGroup, CSSTransition } from 'react-transition-group'
import React, { useRef } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'

// Polyfill for findDOMNode since it was removed in React 19
// react-transition-group v4 still tries to use it as a fallback
if (!ReactDOM.findDOMNode) {
  ReactDOM.findDOMNode = (node) => {
    // If it's already a DOM node, return it
    if (node instanceof Element) {
      return node
    }
    // If it's a ref object with current property
    if (node && node.current) {
      return node.current
    }
    // Otherwise return null
    return null
  }
}

// Wrapper to provide backward compatibility with react-addons-css-transition-group API
// while using the modern react-transition-group library
//
const DEFAULT_TIMEOUT = 500

/**
 * Calculates timeout value for a transition phase.
 * Returns the explicit timeout if provided, falls back to default timeout, or returns 0 if disabled.
 *
 * @param {boolean} isEnabled - Whether this transition phase is enabled
 * @param {number|undefined} explicitTimeout - The explicit timeout value for this phase
 * @param {number} defaultTimeout - The default timeout to use if no explicit value provided
 * @returns {number} The calculated timeout in milliseconds
 */
const calculateTimeout = (isEnabled, explicitTimeout, defaultTimeout) => {
  if (!isEnabled) return 0
  return explicitTimeout ?? defaultTimeout
}

const ReactCSSTransitionGroup = ({
  children,
  transitionName,
  transitionAppear = false,
  transitionEnter = true,
  transitionLeave = true,
  transitionAppearTimeout,
  transitionEnterTimeout,
  transitionLeaveTimeout,
  ...props
}) => {
  // Fallback timeout calculation:
  // Priority order: transitionEnterTimeout -> transitionLeaveTimeout -> DEFAULT_TIMEOUT
  // This maintains backward compatibility with the original API where enter/leave timeouts
  // were used interchangeably when only one was specified
  const fallbackTimeout = transitionEnterTimeout ?? transitionLeaveTimeout ?? DEFAULT_TIMEOUT
  const isTestMode = typeof window !== 'undefined' && window.testMode === true

  const timeout = isTestMode ?
    {
      appear: transitionAppear ? 1 : 0,
      enter: transitionEnter ? 1 : 0,
      exit: transitionLeave ? 1 : 0
    } :
    {
      appear: calculateTimeout(transitionAppear, transitionAppearTimeout, fallbackTimeout),
      enter: calculateTimeout(transitionEnter, transitionEnterTimeout, fallbackTimeout),
      exit: calculateTimeout(transitionLeave, transitionLeaveTimeout, fallbackTimeout)
    }

  return (
    <TransitionGroup {...props}>
      {React.Children.map(children, (child) =>
        child ?
          (
            <CSSTransitionWrapper
              key={child.key}
              timeout={timeout}
              classNames={transitionName}
              appear={transitionAppear}
              enter={transitionEnter}
              exit={transitionLeave}
            >
              {child}
            </CSSTransitionWrapper>
          ) :
          null
      )}
    </TransitionGroup>
  )
}

// Wrapper component to provide nodeRef for React 19 compatibility
const CSSTransitionWrapper = ({ children, ...props }) => {
  const nodeRef = useRef(null)

  return (
    <CSSTransition nodeRef={nodeRef} {...props}>
      <span ref={nodeRef} style={{ display: 'contents' }}>
        {children}
      </span>
    </CSSTransition>
  )
}

CSSTransitionWrapper.propTypes = {
  children: PropTypes.element.isRequired
}

ReactCSSTransitionGroup.propTypes = {
  children: PropTypes.node,
  transitionName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      appear: PropTypes.string,
      appearActive: PropTypes.string,
      appearDone: PropTypes.string,
      enter: PropTypes.string,
      enterActive: PropTypes.string,
      enterDone: PropTypes.string,
      exit: PropTypes.string,
      exitActive: PropTypes.string,
      exitDone: PropTypes.string
    })
  ]),
  transitionAppear: PropTypes.bool,
  transitionEnter: PropTypes.bool,
  transitionLeave: PropTypes.bool,
  transitionAppearTimeout: PropTypes.number,
  transitionEnterTimeout: PropTypes.number,
  transitionLeaveTimeout: PropTypes.number
}

// eslint-disable-next-line no-restricted-exports
export default ReactCSSTransitionGroup
